// src/pages/admin/PanchayathMasterManager.jsx

import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  useDispatch,
  useSelector,
} from "react-redux";

import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Form,
  FormControl,
  InputGroup,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";

import {
  Edit2,
  Eye,
  Plus,
  RefreshCcw,
  Search,
  Trash2,
  X,
} from "react-feather";

import {
  useNavigate,
} from "react-router-dom";

import { toast } from "react-toastify";

import {
  fetchDistrictsThunk,
  fetchTaluksByDistrictThunk,
  fetchPanchayathsThunk,
  deletePanchayathThunk,
  setPmFilters,
  resetPmForm,
} from "../../../store/slices/panchayathMasterSlice";

import "../../../css/master-common.css";

// ============================================================
// STATUS
// ============================================================

const Pill = ({ children }) => (
  <Badge className="mm-pill">
    {children}
  </Badge>
);

const StatusPill = ({
  status,
}) => {
  const isActive =
    Number(status) === 1;

  return (
    <Pill>
      {isActive
        ? "Active"
        : "Inactive"}
    </Pill>
  );
};

// ============================================================
// PAGE
// ============================================================

export default function PanchayathMasterManager() {
  const dispatch =
    useDispatch();

  const navigate =
    useNavigate();

  const {
    districts,
    taluks,
    rows,
    loading,
    deleting,
    error,
    filters,
  } = useSelector(
    (state) =>
      state.panchayathMaster
  );

  const [search, setSearch] =
    useState("");

  // ==========================================================
  // LOAD DISTRICTS
  // ==========================================================

  useEffect(() => {
    dispatch(
      fetchDistrictsThunk()
    );
  }, [dispatch]);

  // ==========================================================
  // LOAD LIST
  // ==========================================================

  useEffect(() => {
    dispatch(
      fetchPanchayathsThunk()
    );
  }, [
    dispatch,
    filters.dist_id,
    filters.taluk_id,
  ]);

  // ==========================================================
  // LOAD TALUKS
  // ==========================================================

  useEffect(() => {
    if (filters.dist_id) {
      dispatch(
        fetchTaluksByDistrictThunk(
          filters.dist_id
        )
      );
    }
  }, [
    dispatch,
    filters.dist_id,
  ]);

  // ==========================================================
  // SEARCH
  // ==========================================================

  const filteredRows =
    useMemo(() => {
      const q = String(
        search || ""
      )
        .trim()
        .toLowerCase();

      const all =
        Array.isArray(rows)
          ? rows
          : [];

      if (!q) {
        return all;
      }

      return all.filter(
        (row) =>
          `
            ${row.pan_name || ""}
            ${row.district_name || ""}
            ${row.taluk_name || ""}
          `
            .toLowerCase()
            .includes(q)
      );
    }, [rows, search]);

  // ==========================================================
  // ADD
  // ==========================================================

  const openAdd = () => {
    dispatch(
      resetPmForm()
    );

    navigate(
      "/settings/panchayath-master/add"
    );
  };


  // ==========================================================
  // VIEW FULL PROFILE
  // ==========================================================

  const openProfile = (
    row
  ) => {
    if (!row?.pan_id) {
      toast.error(
        "Panchayath ID missing"
      );

      return;
    }

    navigate(
      `/settings/panchayath-master/profile/${row.pan_id}`
    );
  };

  // ==========================================================
  // EDIT
  // ==========================================================

  const openEdit = (
    row
  ) => {
    if (!row?.pan_id) {
      toast.error(
        "Panchayath ID missing"
      );

      return;
    }

    navigate(
      `/settings/panchayath-master/edit/${row.pan_id}`
    );
  };

  // ==========================================================
  // DELETE
  // ==========================================================

  const onDelete = (
    row
  ) => {
    if (!row?.pan_id) {
      toast.error(
        "Panchayath ID missing"
      );

      return;
    }

    toast.info(
      ({ closeToast }) => (
        <div>
          <div className="fw-semibold mb-1">
            Delete Panchayath "
            {row.pan_name}"?
          </div>

          <div className="text-muted small mb-2">
            This action cannot be
            undone.
          </div>

          <div className="d-flex gap-2 mt-2">
            <button
              type="button"
              className="btn btn-sm btn-danger"
              onClick={async () => {
                const result =
                  await dispatch(
                    deletePanchayathThunk(
                      row.pan_id
                    )
                  );

                if (
                  result?.meta
                    ?.requestStatus ===
                  "fulfilled"
                ) {
                  toast.success(
                    "Panchayath deleted successfully"
                  );

                  dispatch(
                    fetchPanchayathsThunk()
                  );
                } else {
                  toast.error(
                    result?.payload ||
                      "Failed to delete Panchayath"
                  );
                }

                closeToast();
              }}
            >
              Delete
            </button>

            <button
              type="button"
              className="btn btn-sm btn-secondary"
              onClick={
                closeToast
              }
            >
              Cancel
            </button>
          </div>
        </div>
      ),
      {
        autoClose: false,
        closeOnClick: false,
      }
    );
  };

  // ==========================================================
  // DISTRICT FILTER
  // ==========================================================

  const handleDistrictFilter =
    (e) => {
      const dist_id =
        e.target.value;

      dispatch(
        setPmFilters({
          dist_id,
          taluk_id: "",
        })
      );

      if (dist_id) {
        dispatch(
          fetchTaluksByDistrictThunk(
            dist_id
          )
        );
      }
    };

  // ==========================================================
  // TALUK FILTER
  // ==========================================================

  const handleTalukFilter =
    (e) => {
      dispatch(
        setPmFilters({
          taluk_id:
            e.target.value,
        })
      );
    };

  return (
    <div className="container-fluid px-3 py-4 mm-page">
      {/* HEADER */}

      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h4 className="mb-1 fw-bold mm-title">
            Panchayath Master
          </h4>

          <div className="text-muted small">
            Manage Panchayath
            details
          </div>
        </div>

        <div className="d-flex gap-2">
          <Button
            variant="light"
            className="mm-cancel-btn d-flex align-items-center gap-2"
            onClick={() =>
              dispatch(
                fetchPanchayathsThunk()
              )
            }
            disabled={loading}
          >
            <RefreshCcw
              size={16}
            />

            {loading
              ? "Refreshing..."
              : "Refresh"}
          </Button>

          <Button
            className="mm-primary-btn d-flex align-items-center gap-2"
            onClick={openAdd}
          >
            <Plus size={18} />
            Add Panchayath
          </Button>
        </div>
      </div>

      {/* ERROR */}

      {error ? (
        <Alert
          variant="danger"
          className="mm-card-soft mb-3"
        >
          <div className="fw-bold mb-1">
            Error
          </div>

          <div>
            {String(error)}
          </div>
        </Alert>
      ) : null}

      {/* FILTER */}

      <Card className="mm-card-soft mb-3">
        <Card.Body className="p-3">
          <Row className="g-3 align-items-end">
            <Col
              lg={3}
              md={4}
              sm={12}
            >
              <Form.Label className="fw-bold mb-1">
                District
              </Form.Label>

              <Form.Select
                className="mm-select"
                value={
                  filters.dist_id ||
                  ""
                }
                onChange={
                  handleDistrictFilter
                }
              >
                <option value="">
                  All Districts
                </option>

                {(districts || []).map(
                  (district) => (
                    <option
                      key={
                        district.dist_id
                      }
                      value={
                        district.dist_id
                      }
                    >
                      {
                        district.dist_name
                      }
                    </option>
                  )
                )}
              </Form.Select>
            </Col>

            <Col
              lg={3}
              md={4}
              sm={12}
            >
              <Form.Label className="fw-bold mb-1">
                Taluk
              </Form.Label>

              <Form.Select
                className="mm-select"
                value={
                  filters.taluk_id ||
                  ""
                }
                onChange={
                  handleTalukFilter
                }
                disabled={
                  !filters.dist_id
                }
              >
                <option value="">
                  All Taluks
                </option>

                {(taluks || []).map(
                  (taluk) => (
                    <option
                      key={
                        taluk.taluk_id
                      }
                      value={
                        taluk.taluk_id
                      }
                    >
                      {
                        taluk.taluk_name
                      }
                    </option>
                  )
                )}
              </Form.Select>
            </Col>

            <Col
              lg={6}
              md={4}
              sm={12}
            >
              <Form.Label className="fw-bold mb-1">
                Search
              </Form.Label>

              <InputGroup>
                <InputGroup.Text className="mm-inputgroup-text">
                  <Search
                    size={18}
                    className="text-muted"
                  />
                </InputGroup.Text>

                <FormControl
                  className="mm-input mm-inputgroup-control"
                  placeholder="Search Panchayath / District / Taluk..."
                  value={search}
                  onChange={(e) =>
                    setSearch(
                      e.target.value
                    )
                  }
                />

                {search ? (
                  <Button
                    variant="light"
                    className="mm-clear-btn"
                    onClick={() =>
                      setSearch("")
                    }
                  >
                    <X size={16} />
                  </Button>
                ) : null}
              </InputGroup>
            </Col>

            <Col
              md={12}
              className="text-end"
            >
              <div className="text-muted small">
                Total:{" "}
                <span className="fw-bold">
                  {
                    filteredRows.length
                  }
                </span>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* TABLE */}

      <Card className="mm-card">
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" />

              <div className="mt-2 text-muted">
                Loading Panchayaths...
              </div>
            </div>
          ) : (
            <div className="mm-table-wrap">
              <Table
                hover
                responsive
                className="mm-table mb-0"
              >
                <thead>
                  <tr>
                    <th
                      style={{
                        width: 90,
                      }}
                    >
                      Sl. No.
                    </th>

                    <th
                      style={{
                        width: 120,
                      }}
                    >
                      Status
                    </th>

                    <th>
                      Panchayath
                    </th>

                    <th>
                      District
                    </th>

                    <th>
                      Taluk
                    </th>

                    <th
                      className="mm-actions-th"
                      style={{
                        width: 180,
                        textAlign:
                          "right",
                      }}
                    >
                      Actions
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {filteredRows.length ===
                  0 ? (
                    <tr>
                      <td
                        colSpan={6}
                        className="p-5 text-center"
                      >
                        <div className="mm-empty-icon mx-auto mb-2">
                          <Search
                            size={22}
                          />
                        </div>

                        <div className="fw-bold mb-1">
                          {search
                            ? "No results found"
                            : "No Panchayaths found"}
                        </div>

                        <div className="text-muted">
                          {search
                            ? "No Panchayaths match your search."
                            : "Get started by adding your first Panchayath."}
                        </div>
                      </td>
                    </tr>
                  ) : (
                    filteredRows.map(
                      (
                        row,
                        index
                      ) => (
                        <tr
                          key={
                            row.pan_id
                          }
                        >
                          <td>
                            {index +
                              1}
                          </td>

                          <td>
                            <StatusPill
                              status={
                                row.status
                              }
                            />
                          </td>

                          <td className="fw-bold">
                            {row.pan_name ||
                              "—"}
                          </td>

                          <td>
                            {row.district_name ||
                              "—"}
                          </td>

                          <td>
                            {row.taluk_name ||
                              "—"}
                          </td>

                          <td
                            className="mm-actions-td"
                            style={{
                              textAlign:
                                "right",
                            }}
                          >
                            <div className="d-inline-flex gap-2">
                              <Button
                                variant="light"
                                className="mm-icon-btn primary"
                                onClick={() =>
                                  openProfile(
                                    row
                                  )
                                }
                                title="View Full Profile"
                              >
                                <Eye
                                  size={
                                    16
                                  }
                                />
                              </Button>

                              <Button
                                variant="light"
                                className="mm-icon-btn primary"
                                onClick={() =>
                                  openEdit(
                                    row
                                  )
                                }
                                title="Edit"
                              >
                                <Edit2
                                  size={
                                    16
                                  }
                                />
                              </Button>

                              <Button
                                variant="light"
                                className="mm-icon-btn danger"
                                onClick={() =>
                                  onDelete(
                                    row
                                  )
                                }
                                disabled={
                                  deleting
                                }
                                title="Delete"
                              >
                                <Trash2
                                  size={
                                    16
                                  }
                                />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      )
                    )
                  )}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
}